import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import environment from '../config/environment';

const RegistrarAsistencia = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [maxAccuracyMeters, setMaxAccuracyMeters] = useState(30);
  const [simulatedGPS, setSimulatedGPS] = useState(null);
  const gpsCoordsRef = useRef(null); // Referencia para acceder a gpsCoords sin causar re-renders
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [gpsMessage, setGpsMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDistanceErrorModal, setShowDistanceErrorModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [distanceError, setDistanceError] = useState({ distance: 0, maxDistance: 0, lat: null, lng: null });
  const [asistenciaInfoModal, setAsistenciaInfoModal] = useState({
    supervisor: '',
    puesto: '',
    vigilante: '',
    fecha: '',
    hora: ''
  });
  
  // Estados para puestos
  const [puestos, setPuestos] = useState([]);
  const [selectedPuesto, setSelectedPuesto] = useState('');
  const [puestoInfo, setPuestoInfo] = useState(null);
  
  // Estados del formulario
  const [checks, setChecks] = useState([]);
  const [selectedChecks, setSelectedChecks] = useState([]);
  const [comentarios, setComentarios] = useState('');
  const [cedulaBusqueda, setCedulaBusqueda] = useState('');
  const [personalEncontrado, setPersonalEncontrado] = useState(null);
  const [buscandoPersonal, setBuscandoPersonal] = useState(false);
  const [verificacionVigilante, setVerificacionVigilante] = useState(null);
  const [discrepanciaInfo, setDiscrepanciaInfo] = useState(null);
  const [reportandoDiscrepancia, setReportandoDiscrepancia] = useState(false);
  const [auditoriaDiscrepanciaId, setAuditoriaDiscrepanciaId] = useState(null);
  const [fotoArchivo, setFotoArchivo] = useState(null);
  const [fotoPreview, setFotoPreview] = useState('');
  const capturaInputId = useMemo(() => `asistencia-foto-captura-${uuidv4()}`, []);
  
  // Estados GPS
  const [gpsCoords, setGpsCoords] = useState(null);
  const [validatingGps, setValidatingGps] = useState(false);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [watchingGps, setWatchingGps] = useState(false);
  const [gpsWatchId, setGpsWatchId] = useState(null);
  const [gpsLastUpdate, setGpsLastUpdate] = useState(null); // Timestamp de última actualización
  const [gpsActive, setGpsActive] = useState(false);
  const gpsLastUpdateRef = useRef(null);

  // Limpiar formulario al entrar desde el menú
  useEffect(() => {
    if (location.pathname !== '/supervision/registrar-asistencia') return;

    localStorage.removeItem('supervision_selected_puesto');
      
      // Limpiar todos los estados del formulario
      setSelectedPuesto('');
      setPuestoInfo(null);
      setChecks([]);
      setSelectedChecks([]);
      setComentarios('');
      setCedulaBusqueda('');
      setPersonalEncontrado(null);
      setVerificacionVigilante(null);
      setDiscrepanciaInfo(null);
      setAuditoriaDiscrepanciaId(null);
      setFotoArchivo(null);
      setFotoPreview('');
      setError('');
      setSuccess('');
      setGpsMessage('');
      setShowSuccessModal(false);
      setShowDistanceErrorModal(false);
      
      // Limpiar estados GPS
      setGpsCoords(null);
      setGpsAccuracy(null);
      setGpsLastUpdate(null);
      setGpsActive(false);
      setWatchingGps(false);
      gpsCoordsRef.current = null;
      gpsLastUpdateRef.current = null;
      
      // Detener monitoreo GPS si está activo
      if (navigator.geolocation) {
        // Limpiar cualquier watch activo
        setGpsWatchId((currentWatchId) => {
          if (currentWatchId !== null && currentWatchId !== 999999) {
            navigator.geolocation.clearWatch(currentWatchId);
          }
          return null;
        });
      }
  }, [location.pathname]);

  // Cargar configuración de GPS del backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(getApiUrl('SUPERVISION', 'CONFIG'), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        if (response.ok && data.success) {
          if (data.data?.gpsMaxAccuracy) {
            setMaxAccuracyMeters(data.data.gpsMaxAccuracy);
          }
          // Si hay GPS simulado configurado (solo en desarrollo)
          if (data.data?.simulatedGPS?.enabled) {
            setSimulatedGPS(data.data.simulatedGPS);
            console.log('📍 GPS Simulado activado para desarrollo:', data.data.simulatedGPS);
          }
        }
      } catch (error) {
        console.error('Error cargando configuración GPS:', error);
        // Usar valor por defecto si falla
      }
    };
    fetchConfig();
  }, []);

  // Cargar puestos al montar
  useEffect(() => {
    if (user?.rol !== 'S') {
      setError('No tienes permisos para acceder a esta sección');
      setLoading(false);
      return;
    }

    const fetchPuestos = async () => {
      try {
        setLoading(true);
        setError('');
        
        const url = new URL(getApiUrl('SUPERVISION', 'LISTAR_PUESTOS'));
        url.searchParams.set('username', user?.username || '');
        url.searchParams.set('appid', user?.appid || '');

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        const data = await response.json();

        if (response.ok && data.success) {
          const lista = data.data || [];
          setPuestos(lista);

          const savedPuesto = localStorage.getItem('supervision_selected_puesto');
          if (savedPuesto && lista.find((p) => p.id.toString() === savedPuesto)) {
            setSelectedPuesto(savedPuesto);
          }
        } else {
          setError(data.error || 'Error al cargar los puestos de servicio');
        }
      } catch (error) {
        console.error('Error cargando puestos:', error);
        setError('Error de conexión al cargar los puestos de servicio');
      } finally {
        setLoading(false);
      }
    };

    fetchPuestos();
  }, [user]);

  // Cargar checks cuando se selecciona un puesto
  useEffect(() => {
    if (!selectedPuesto) return;

    const fetchChecks = async () => {
      try {
        setError('');
        const puesto = puestos.find(p => p.id.toString() === selectedPuesto);
        if (!puesto) return;

        setPuestoInfo(puesto);
        localStorage.setItem('supervision_selected_puesto', selectedPuesto);

        // El puestoId es el id_generado del puesto, que se usa para buscar checks
        const url = new URL(getApiUrl('SUPERVISION', 'LISTAR_CHECKS'));
        url.searchParams.set('username', user?.username || '');
        url.searchParams.set('puestoId', selectedPuesto); // Este es el id_generado del puesto

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        const data = await response.json();

        if (response.ok && data.success) {
          if (data.data && data.data.length > 0) {
            setChecks(data.data);
            setSelectedChecks([]);
          } else {
            setError(data.error || 'No se ha encontrado una lista de supervisión para este puesto. Por favor contactar administrador.');
            setChecks([]);
          }
        } else {
          setError(data.error || 'Error al cargar la lista de supervisión');
          setChecks([]);
        }
      } catch (error) {
        console.error('Error cargando checks:', error);
        setError('Error de conexión al cargar la lista de supervisión');
        setChecks([]);
      }
    };

    fetchChecks();
    setCedulaBusqueda('');
    setPersonalEncontrado(null);
    setVerificacionVigilante(null);
    setDiscrepanciaInfo(null);
    setAuditoriaDiscrepanciaId(null);
  }, [selectedPuesto, puestos, user]);

  // Limpiar URL de preview cuando se desmonte o cambie
  useEffect(() => {
    if (!fotoPreview) return undefined;
    return () => {
      URL.revokeObjectURL(fotoPreview);
    };
  }, [fotoPreview]);

  // Función para optimizar imágenes
  const optimizeImage = useCallback((file, { maxWidth = 1024, quality = 0.7 } = {}) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const img = new Image();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      reader.onerror = reject;

      img.onload = () => {
        const scale = Math.min(maxWidth / img.width, 1);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('No se pudo optimizar la imagen.'));
              return;
            }
            const baseName = file.name.replace(/\.[^/.]+$/, '');
            const optimizedFile = new File([blob], `${baseName}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;

            resolve(optimizedFile);
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Función para manejar el cambio de archivo de foto
  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0] || null;

      if (!file) {
        setFotoArchivo(null);
        setFotoPreview('');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Solo se permiten imágenes para la foto.');
        event.target.value = '';
        return;
      }

      let finalFile = file;
      const ONE_MB = 1024 * 1024;

      try {
        if (file.size > ONE_MB) {
          const optimized = await optimizeImage(file, { maxWidth: 1024, quality: 0.7 });
          console.log(`Original: ${(file.size / 1024).toFixed(1)} KB`);
          console.log(`Optimizada: ${(optimized.size / 1024).toFixed(1)} KB`);
          finalFile = optimized;
        } else {
          console.log(`Imagen menor a 1MB, se enviará sin cambios (${(file.size / 1024).toFixed(1)} KB).`);
        }
      } catch (optimizationError) {
        console.error('Error optimizando imagen:', optimizationError);
        setError('No se pudo optimizar la imagen. Intenta con otra foto.');
        event.target.value = '';
        return;
      }

      setFotoArchivo(finalFile);
      setFotoPreview(URL.createObjectURL(finalFile));
      setError(''); // Limpiar errores previos
    },
    [optimizeImage]
  );

  const vigilanteVerificado = verificacionVigilante === 'ok' || verificacionVigilante === 'reportada';

  const handleVerificarCedula = async () => {
    if (!cedulaBusqueda.trim() || !selectedPuesto) {
      return;
    }

    setBuscandoPersonal(true);
    setError('');
    setPersonalEncontrado(null);
    setVerificacionVigilante(null);
    setDiscrepanciaInfo(null);
    setAuditoriaDiscrepanciaId(null);
    setSuccess('');

    try {
      const url = new URL(getApiUrl('SUPERVISION', 'VERIFICAR_VIGILANTE_PUESTO'));
      url.searchParams.set('username', user?.username || '');
      url.searchParams.set('puestoId', selectedPuesto);
      url.searchParams.set('cedula', cedulaBusqueda.trim());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Error al verificar la cédula');
        setPersonalEncontrado(null);
        setVerificacionVigilante(null);
        return;
      }

      const { coincide, requiere_reporte, ingresado, pht, mensaje } = data.data;

      setPersonalEncontrado({ nombres: ingresado.nombre, cedula: ingresado.cedula });

      if (coincide) {
        setVerificacionVigilante('ok');
        setDiscrepanciaInfo(null);
        setSuccess(mensaje || 'Cédula verificada: coincide con el historial de trabajo.');
      } else if (requiere_reporte) {
        setVerificacionVigilante('discrepancia');
        setDiscrepanciaInfo({ ingresado, pht, mensaje });
      }
    } catch (err) {
      console.error('Error verificando cédula:', err);
      setError('Error de conexión al verificar la cédula');
    } finally {
      setBuscandoPersonal(false);
    }
  };

  const handleReportarDiscrepancia = () => {
    if (!cedulaBusqueda.trim() || !selectedPuesto || !discrepanciaInfo) {
      return;
    }
    setVerificacionVigilante('reportada');
    setSuccess('Discrepancia registrada. Complete el formulario para enviar.');
  };
  // Iniciar monitoreo continuo de GPS para mostrar precisión en tiempo real
  const iniciarMonitoreoGPS = useCallback(() => {
    // Si hay GPS simulado configurado (solo en desarrollo), usarlo
    if (simulatedGPS && simulatedGPS.enabled) {
      setWatchingGps(true);
      setGpsMessage('Obteniendo coordenadas GPS (SIMULADO)...');
      
      // Simular obtención de GPS con un pequeño delay
      setTimeout(() => {
        const now = Date.now();
        const simulatedCoords = {
          lat: simulatedGPS.lat,
          lng: simulatedGPS.lng,
          acu: simulatedGPS.accuracy,
          timestamp: now
        };
        
        setGpsAccuracy(simulatedGPS.accuracy);
        setGpsLastUpdate(now);
        setGpsActive(true);
        setGpsCoords(simulatedCoords);
        gpsCoordsRef.current = simulatedCoords;
        
        const precisionText = simulatedGPS.accuracy < 10 
          ? `Precisión excelente (SIMULADO): ${simulatedGPS.accuracy.toFixed(1)} m` 
          : simulatedGPS.accuracy < 20 
          ? `Precisión buena (SIMULADO): ${simulatedGPS.accuracy.toFixed(1)} m`
          : simulatedGPS.accuracy < 50
          ? `Precisión aceptable (SIMULADO): ${simulatedGPS.accuracy.toFixed(1)} m`
          : `Precisión baja (SIMULADO): ${simulatedGPS.accuracy.toFixed(1)} m`;
        
        setGpsMessage(precisionText);
      }, 500);
      
      // Retornar un ID simulado
      return 999999;
    }
    
    // GPS real (producción)
    if (!navigator.geolocation) {
      setError('Geolocalización no soportada en este navegador');
      return null;
    }

    setWatchingGps(true);
    setGpsMessage('Obteniendo coordenadas GPS...');

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const accuracy = position.coords.accuracy;
        const now = Date.now();
        
        setGpsAccuracy(accuracy);
        setGpsLastUpdate(now);
        gpsLastUpdateRef.current = now; // Actualizar referencia también
        setGpsActive(true); // GPS está activo y funcionando
        setWatchingGps(true); // Asegurar que watchingGps esté en true cuando recibimos coordenadas
        const newCoords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          acu: accuracy,
          timestamp: now
        };
        setGpsCoords(newCoords);
        gpsCoordsRef.current = newCoords; // Actualizar referencia
        
        // Limpiar cualquier error previo cuando recibimos coordenadas exitosamente
        setError('');
        
        // Actualizar mensaje con la precisión
        if (accuracy) {
          const precisionText = accuracy < 10 
            ? `Precisión excelente: ${accuracy.toFixed(1)} m` 
            : accuracy < 20 
            ? `Precisión buena: ${accuracy.toFixed(1)} m`
            : accuracy < 50
            ? `Precisión aceptable: ${accuracy.toFixed(1)} m`
            : `Precisión baja: ${accuracy.toFixed(1)} m`;
          
          setGpsMessage(precisionText);
        } else {
          setGpsMessage('Coordenadas obtenidas');
        }
      },
      (error) => {
        // Verificar si tenemos coordenadas recientes antes de marcar como inactivo
        const hasRecentCoords = gpsCoordsRef.current && gpsLastUpdateRef.current && 
          (Date.now() - gpsLastUpdateRef.current) < 60000; // Menos de 60 segundos
        
        // Solo limpiar coordenadas si es un error de permiso denegado
        if (error.code === error.PERMISSION_DENIED) {
          setWatchingGps(false);
          setGpsActive(false);
          setGpsCoords(null);
          setGpsAccuracy(null);
          setGpsLastUpdate(null);
          gpsLastUpdateRef.current = null; // Limpiar referencia también
          gpsCoordsRef.current = null; // Limpiar referencia también
        } else if (hasRecentCoords) {
          // Si tenemos coordenadas recientes, mantener el estado como activo
          // Los errores temporales (POSITION_UNAVAILABLE, TIMEOUT) no deberían desactivar el GPS
          // si ya tenemos coordenadas válidas
          setGpsActive(true);
          setWatchingGps(true);
          // No mostrar error si tenemos coordenadas recientes
          return;
        } else {
          // No hay coordenadas recientes, marcar como inactivo
          setWatchingGps(false);
          setGpsActive(false);
        }
        
        let errorMessage = 'Error al obtener la ubicación GPS';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permiso de geolocalización denegado. Por favor, habilita la geolocalización en tu navegador.';
            break;
          case error.POSITION_UNAVAILABLE:
              errorMessage = 'Ubicación no disponible. Verifica que el GPS esté activado.';
            break;
          case error.TIMEOUT:
              errorMessage = 'Tiempo de espera agotado al obtener la ubicación.';
            break;
          default:
              errorMessage = `Error al obtener GPS: ${error.message}`;
            break;
        }
        // Solo mostrar error si no tenemos coordenadas recientes
        if (!hasRecentCoords) {
          setError(errorMessage);
          setGpsMessage('');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0 // Cambiar a 0 para no rechazar ubicaciones en Mac
      }
    );

    setGpsWatchId(watchId);
    return watchId;
  }, [simulatedGPS]); // Agregar simulatedGPS como dependencia

  // Activar GPS automáticamente cuando se selecciona un puesto (solo en producción o si no hay GPS simulado)
  useEffect(() => {
    // Solo activar automáticamente si:
    // 1. Hay un puesto seleccionado
    // 2. No hay GPS simulado activo (o estamos en producción)
    // 3. No hay un watchId activo ya
    // 4. El GPS no está siendo monitoreado actualmente
    if (selectedPuesto && checks.length > 0 && !watchingGps && gpsWatchId === null) {
      // En producción, activar automáticamente
      // En desarrollo, solo activar si no hay GPS simulado configurado
      const shouldAutoActivate = environment.isProduction || !simulatedGPS || !simulatedGPS.enabled;
      
      if (shouldAutoActivate && navigator.geolocation) {
        // Pequeño delay para asegurar que el componente esté completamente montado
        const timer = setTimeout(() => {
          setError(''); // Limpiar errores previos
          iniciarMonitoreoGPS();
        }, 500);
        
        return () => clearTimeout(timer);
      }
    }
  }, [selectedPuesto, checks.length, watchingGps, gpsWatchId, simulatedGPS, iniciarMonitoreoGPS]);

  // Detener monitoreo de GPS
  const detenerMonitoreoGPS = useCallback(() => {
    if (gpsWatchId !== null) {
      // Solo llamar clearWatch si no es el ID simulado
      if (gpsWatchId !== 999999 && navigator.geolocation) {
        navigator.geolocation.clearWatch(gpsWatchId);
      }
      setGpsWatchId(null);
    }
    setWatchingGps(false);
  }, [gpsWatchId]);

  // Limpiar monitoreo GPS al desmontar o cambiar de puesto
  useEffect(() => {
    return () => {
      detenerMonitoreoGPS();
    };
  }, [detenerMonitoreoGPS]);

  // Monitorear si el GPS se apaga después de haber estado activo
  // SOLO limpiar coordenadas si el GPS se desactiva Y no tenemos watchId activo Y no hay coordenadas recientes
  // Esto evita limpiar coordenadas válidas cuando watchPosition puede dejar de emitir actualizaciones temporalmente
  useEffect(() => {
    // Si tenemos un watchId activo, el GPS está siendo monitoreado, no limpiar
    if (gpsWatchId !== null) {
      return;
    }
    
    // Si tenemos coordenadas recientes (menos de 60 segundos), mantenerlas aunque el estado diga inactivo
    if (gpsCoords && gpsLastUpdate) {
      const ageSeconds = (Date.now() - gpsLastUpdate) / 1000;
      if (ageSeconds < 60) {
        // Coordenadas recientes, mantener el estado como activo
        if (!gpsActive) {
          setGpsActive(true);
        }
        if (!watchingGps) {
          setWatchingGps(true);
        }
        return;
      }
    }
    
    // SOLO limpiar si realmente no hay watchId Y no hay coordenadas recientes
    if (!gpsActive && !watchingGps && gpsCoords && gpsWatchId === null) {
      // Verificar una vez más si las coordenadas son muy antiguas antes de limpiar
      if (gpsLastUpdate) {
        const ageSeconds = (Date.now() - gpsLastUpdate) / 1000;
        if (ageSeconds > 60) {
      // GPS realmente se apagó - limpiar coordenadas
      setGpsCoords(null);
      setGpsAccuracy(null);
      setGpsLastUpdate(null);
          gpsLastUpdateRef.current = null; // Limpiar referencia también
      gpsCoordsRef.current = null; // Limpiar referencia también
      setError('El GPS se ha desactivado. Por favor, reactiva el GPS para continuar.');
    }
      }
    }
  }, [gpsActive, watchingGps, gpsCoords, gpsWatchId, gpsLastUpdate]);

  // Obtener coordenadas GPS (versión simple para cuando ya tenemos las coordenadas)
  const obtenerGPS = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalización no soportada en este navegador'));
        return;
      }

      // Si ya tenemos coordenadas del monitoreo, usarlas
      if (gpsCoords && gpsCoords.acu !== null) {
        resolve(gpsCoords);
        return;
      }

      // Timeout adicional para evitar que se quede colgado
      const timeoutId = setTimeout(() => {
        reject(new Error('Tiempo de espera agotado. Por favor, verifica que la geolocalización esté habilitada.'));
      }, 15000); // 15 segundos total

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          const accuracy = position.coords.accuracy;
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            acu: accuracy || null
          });
        },
        (error) => {
          clearTimeout(timeoutId);
          let errorMessage = 'Error al obtener la ubicación GPS';
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Permiso de geolocalización denegado. Por favor, habilita la geolocalización en tu navegador.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Ubicación no disponible. Verifica que el GPS esté activado.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Tiempo de espera agotado al obtener la ubicación.';
              break;
            default:
              errorMessage = `Error al obtener GPS: ${error.message}`;
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 12000, // 12 segundos para el API de geolocalización
          maximumAge: 0
        }
      );
    });
  }, [gpsCoords]);

  // Validar GPS en el servidor
  const validarGPS = async (coords) => {
    try {
      setValidatingGps(true);
      setGpsMessage('Validando coordenadas GPS...');

      const response = await fetch(getApiUrl('SUPERVISION', 'VALIDAR_GPS'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: coords.lat,
          lng: coords.lng,
          acu: coords.acu,
          puestoId: selectedPuesto
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setGpsMessage('Coordenadas válidas');
        return true;
      } else {
        setGpsMessage(`Error: ${data.error || 'Coordenadas inválidas'}`);
        return false;
      }
    } catch (error) {
      console.error('Error validando GPS:', error);
      setGpsMessage('Error al validar coordenadas GPS');
      return false;
    } finally {
      setValidatingGps(false);
    }
  };

  const handleCheckChange = (check) => {
    setSelectedChecks(prev => {
      const checkExists = prev.find(c => c.id === check.id);
      if (checkExists) {
        return prev.filter(c => c.id !== check.id);
      } else {
        return [...prev, { id: check.id, descripcion: check.descripcion }];
      }
    });
  };

  // Cerrar modal de éxito y reiniciar formulario
  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
    setSuccess('');
    setAsistenciaInfoModal({
      supervisor: '',
      puesto: '',
      vigilante: '',
      fecha: '',
      hora: ''
    });
    
    // Reiniciar formulario para registrar un nuevo puesto
    setSelectedPuesto('');
    setPuestoInfo(null);
    setSelectedChecks([]);
    setComentarios('');
    setCedulaBusqueda('');
    setPersonalEncontrado(null);
    setVerificacionVigilante(null);
    setDiscrepanciaInfo(null);
    setAuditoriaDiscrepanciaId(null);
    setFotoArchivo(null);
    setFotoPreview('');
    setGpsCoords(null);
    setGpsAccuracy(null);
    setGpsLastUpdate(null);
    setGpsActive(false);
    setWatchingGps(false);
    setGpsMessage('');
    gpsCoordsRef.current = null;
    gpsLastUpdateRef.current = null;
    
    // Detener monitoreo GPS si está activo
    if (gpsWatchId !== null && gpsWatchId !== 999999 && navigator.geolocation) {
      navigator.geolocation.clearWatch(gpsWatchId);
      setGpsWatchId(null);
    }
    
    // Limpiar error
    setError('');
  }, [gpsWatchId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPuesto) {
      setError('Por favor selecciona un puesto de servicio');
      return;
    }

    // Validar vigilante verificado contra historial de trabajo
    const vigilanteSeleccionado = personalEncontrado?.nombres;

    if (!vigilanteVerificado || !vigilanteSeleccionado) {
      setError('Debes verificar la cédula del vigilante antes de enviar el formulario');
      return;
    }

    if (checks.length === 0) {
      setError('No se ha encontrado una lista de supervisión para este puesto. Por favor contactar administrador.');
      setShowErrorModal(true);
      return;
    }

    // Validar que al menos un item del checklist esté marcado
    if (selectedChecks.length === 0) {
      setError('Debes marcar al menos un item de la lista de supervisión');
      setShowErrorModal(true);
      return;
    }

    // Validar que la foto de supervisión sea obligatoria
    if (!fotoArchivo) {
      setError('Debes capturar una foto de supervisión');
      setShowErrorModal(true);
      return;
    }

    // Validar que los comentarios sean obligatorios
    if (!comentarios || !comentarios.trim()) {
      setError('Los comentarios son obligatorios');
      setShowErrorModal(true);
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    setGpsMessage('');

    try {
      // SEGURIDAD: Obtener ubicación FRESCA justo antes de enviar (no usar coordenadas antiguas)
      setGpsMessage('Obteniendo ubicación GPS actualizada...');
      let coords;
      
      // Si hay GPS simulado, usarlo directamente
      if (simulatedGPS && simulatedGPS.enabled) {
        coords = {
          lat: simulatedGPS.lat,
          lng: simulatedGPS.lng,
          acu: simulatedGPS.accuracy,
          timestamp: Date.now()
        };
        setGpsMessage(`Precisión (SIMULADO): ${simulatedGPS.accuracy.toFixed(1)} m. Validando...`);
      } else {
        const coordsRecientes = gpsCoordsRef.current && gpsLastUpdateRef.current &&
          (Date.now() - gpsLastUpdateRef.current) / 1000 < 30;
        if (coordsRecientes && gpsCoordsRef.current) {
          coords = {
            ...gpsCoordsRef.current,
            timestamp: gpsLastUpdateRef.current
          };
          setGpsMessage(`Precisión: ${coords.acu?.toFixed(1) ?? '?'} m. Validando...`);
        } else {
        try {
          // Obtener nueva ubicación con timeout corto para asegurar que es actual
          coords = await new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
              reject(new Error('Geolocalización no soportada'));
              return;
            }

            const timeoutId = setTimeout(() => {
              reject(new Error('Tiempo de espera agotado. Verifica que el GPS esté activado.'));
            }, 10000);

            navigator.geolocation.getCurrentPosition(
              (position) => {
                clearTimeout(timeoutId);
                const accuracy = position.coords.accuracy;
                resolve({
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                  acu: accuracy || null,
                  timestamp: Date.now()
                });
              },
              (error) => {
                clearTimeout(timeoutId);
                let errorMessage = 'Error al obtener la ubicación GPS';
                switch(error.code) {
                  case error.PERMISSION_DENIED:
                    errorMessage = 'Permiso de geolocalización denegado. El GPS debe estar activo para registrar asistencia.';
                    break;
                  case error.POSITION_UNAVAILABLE:
                    errorMessage = 'Ubicación no disponible. Verifica que el GPS esté activado en tu dispositivo.';
                    break;
                  case error.TIMEOUT:
                    errorMessage = 'Tiempo de espera agotado. El GPS debe estar activo para registrar asistencia.';
                    break;
                  default:
                    errorMessage = `Error al obtener GPS: ${error.message}`;
                    break;
                }
                reject(new Error(errorMessage));
              },
              {
                enableHighAccuracy: true,
                timeout: 8000,
                maximumAge: 0 // No usar ubicaciones en caché
              }
            );
          });
        } catch (gpsError) {
          console.error('Error obteniendo GPS:', gpsError);
          setGpsMessage('');
          setError(gpsError.message || 'No se pudo obtener la ubicación GPS. El GPS debe estar activo para registrar asistencia.');
          setShowErrorModal(true);
          setSubmitting(false);
          return;
        }
        }
      }

      // SEGURIDAD: Validar que el GPS esté activo (verificar que watchPosition sigue funcionando)
      // En modo simulado, saltar esta validación
      if (!simulatedGPS && (!gpsActive || !watchingGps)) {
        setGpsMessage('');
        setError('El GPS no está activo. Por favor, activa el GPS y espera a obtener una ubicación válida antes de enviar.');
        setShowErrorModal(true);
        setSubmitting(false);
        return;
      }

      // SEGURIDAD: Validar que la ubicación sea reciente (menos de 30 segundos)
      const now = Date.now();
      const locationAge = coords.timestamp ? (now - coords.timestamp) / 1000 : Infinity;
      
      if (locationAge > 30) {
        setGpsMessage('');
        setError('La ubicación GPS es muy antigua. Por favor, obtén una nueva ubicación antes de enviar.');
        setShowErrorModal(true);
        setSubmitting(false);
        return;
      }

      // SEGURIDAD: Validar precisión máxima (configurable desde backend .env)
      if (!coords.acu || coords.acu > maxAccuracyMeters) {
        setGpsMessage('');
        setError(`La precisión del GPS es insuficiente (${coords.acu ? coords.acu.toFixed(1) : 'desconocida'} m). Se requiere una precisión de máximo ${maxAccuracyMeters} metros. Por favor, espera a mejorar la señal GPS.`);
        setShowErrorModal(true);
        setSubmitting(false);
        return;
      }

      setGpsMessage(`Precisión: ${coords.acu.toFixed(1)} m. Validando...`);

      // Validar GPS en el servidor
      const gpsValido = await validarGPS(coords);
      if (!gpsValido) {
        setSubmitting(false);
        return;
      }

      // SEGURIDAD: Validar ubicación GPS respecto al puesto
      setGpsMessage('Validando ubicación respecto al puesto...');
      try {
        const ubicacionResponse = await fetch(getApiUrl('SUPERVISION', 'VALIDAR_UBICACION_PUESTO'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            puestoId: selectedPuesto,
            lat: coords.lat,
            lng: coords.lng
          })
        });

        const ubicacionData = await ubicacionResponse.json();

        if (!ubicacionResponse.ok || !ubicacionData.success) {
          // Si está muy lejos del puesto, mostrar modal de error
          if (ubicacionData.tooFar) {
            setDistanceError({
              distance: ubicacionData.distance || 0,
              maxDistance: ubicacionData.maxDistance || 100,
              lat: coords.lat,
              lng: coords.lng
            });
            setShowDistanceErrorModal(true);
            setGpsMessage('');
            setSubmitting(false);
            return;
          } else {
            setGpsMessage('');
            setError(ubicacionData.error || 'Error al validar ubicación del puesto');
            setShowErrorModal(true);
            setSubmitting(false);
            return;
          }
        }

        // Si es la primera vez, informar
        if (ubicacionData.firstTime) {
          setGpsMessage('Coordenadas del puesto registradas por primera vez. Continuando...');
        } else if (ubicacionData.withinRange) {
          setGpsMessage(`Ubicación válida (${ubicacionData.distance.toFixed(1)} m del puesto). Continuando...`);
        }
      } catch (ubicacionError) {
        console.error('Error validando ubicación del puesto:', ubicacionError);
        setGpsMessage('');
        setError('Error al validar ubicación respecto al puesto. Inténtalo de nuevo.');
        setShowErrorModal(true);
        setSubmitting(false);
        return;
      }

      // Enviar formulario (FormData si hay foto de supervisión)
      let response;
      if (fotoArchivo) {
        const formData = new FormData();
        formData.append('username', user?.username || '');
        formData.append('appid', user?.appid || '');
        formData.append('puestoId', selectedPuesto);
        formData.append('vigilanteTurno', vigilanteSeleccionado.trim());
        formData.append('sancionar', 'false');
        formData.append('checkList', JSON.stringify(selectedChecks));
        formData.append('comentarios', comentarios.trim());
        formData.append('lat', coords.lat.toString());
        formData.append('lng', coords.lng.toString());
        formData.append('acu', coords.acu?.toString() || '');
        if (verificacionVigilante === 'reportada' && discrepanciaInfo) {
          formData.append('hayDiscrepancia', 'true');
          formData.append('cedulaIngresada', cedulaBusqueda.trim());
          formData.append('discrepanciaPht', JSON.stringify(discrepanciaInfo.pht));
          formData.append('discrepanciaIngresado', JSON.stringify(discrepanciaInfo.ingresado));
        }
        formData.append('foto', fotoArchivo);

        response = await fetch(getApiUrl('SUPERVISION', 'REGISTRAR_ASISTENCIA'), {
          method: 'POST',
          body: formData
        });
      } else {
        const jsonBody = {
          username: user?.username,
          appid: user?.appid,
          puestoId: selectedPuesto,
          vigilanteTurno: vigilanteSeleccionado.trim(),
          sancionar: false,
          tipoSancion: null,
          checkList: selectedChecks,
          comentarios: comentarios.trim(),
          lat: coords.lat,
          lng: coords.lng,
          acu: coords.acu
        };
        if (verificacionVigilante === 'reportada' && discrepanciaInfo) {
          jsonBody.hayDiscrepancia = true;
          jsonBody.cedulaIngresada = cedulaBusqueda.trim();
          jsonBody.discrepanciaPht = discrepanciaInfo.pht;
          jsonBody.discrepanciaIngresado = discrepanciaInfo.ingresado;
        }
        response = await fetch(getApiUrl('SUPERVISION', 'REGISTRAR_ASISTENCIA'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(jsonBody)
        });
      }

      const data = await response.json();

      if (response.ok && data.success) {
        // Preparar información para el modal
        const fechaActual = new Date().toISOString().split('T')[0];
        const horaActual = new Date().toTimeString().split(' ')[0].substring(0, 5);
        
        // Parsear fecha y hora desde el datetime del backend (formato: YYYY-MM-DD HH:mm:ss)
        const fechaHoraBackend = data.data?.fecha || '';
        let fechaFormateada = '';
        let horaFormateada = '';
        
        if (fechaHoraBackend) {
          const [fechaPart, horaPart] = fechaHoraBackend.split(' ');
          fechaFormateada = fechaPart || '';
          horaFormateada = horaPart ? horaPart.substring(0, 5) : ''; // Tomar solo HH:mm
        } else {
          // Fallback si no hay fecha del backend
          fechaFormateada = fechaActual;
          horaFormateada = horaActual;
        }
        
        setAsistenciaInfoModal({
          supervisor: data.data?.supervisor || user?.username || '',
          puesto: data.data?.puesto || puestoInfo?.nombre || selectedPuesto || '',
          vigilante: vigilanteSeleccionado.trim(),
          fecha: fechaFormateada,
          hora: horaFormateada
        });

        setSuccess('Asistencia registrada exitosamente');
        setShowSuccessModal(true);
        
        // Limpiar formulario inmediatamente (no afecta el modal)
        setSelectedChecks([]);
        setComentarios('');
        setPersonalEncontrado(null);
        setCedulaBusqueda('');
        setVerificacionVigilante(null);
        setDiscrepanciaInfo(null);
        setAuditoriaDiscrepanciaId(null);
        setGpsMessage('');
        setError(''); // Limpiar errores previos
      } else {
        // Mostrar error más detallado si está disponible
        const errorMsg = data.error || 'Error al registrar la asistencia';
        const details = data.details ? `\nDetalles: ${data.details}` : '';
        const sqlMessage = data.sqlMessage ? `\nSQL: ${data.sqlMessage}` : '';
        setError(`${errorMsg}${details}${sqlMessage}`);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error al registrar asistencia:', error);
      if (error.message.includes('Geolocalización')) {
        setError('No se pudo obtener la ubicación GPS. Por favor habilita la geolocalización en tu dispositivo.');
      } else {
        setError('Error de conexión. Inténtalo de nuevo.');
      }
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCambiarPuesto = () => {
    setSelectedPuesto('');
    setPuestoInfo(null);
    setChecks([]);
    setSelectedChecks([]);
    setComentarios('');
    setCedulaBusqueda('');
    setPersonalEncontrado(null);
    setVerificacionVigilante(null);
    setDiscrepanciaInfo(null);
    setAuditoriaDiscrepanciaId(null);
    setFotoArchivo(null);
    setFotoPreview('');
    setError('');
    setSuccess('');
    setGpsMessage('');
    localStorage.removeItem('supervision_selected_puesto');
  };

  const supervisorNombre = user?.login_nombre || user?.firstName || user?.username || '';

  return (
    <Layout>
      <div className="p-4 sm:p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
              Registrar Asistencia
            </h1>

            {/* Información del supervisor y punto */}
            {puestoInfo && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-md font-semibold text-red-600 mb-1">
                  Punto: {environment.puntoChar}{puestoInfo.id_punto}
                </h3>
                <h2 className="text-lg font-semibold text-indigo-700 mb-2">
                  {puestoInfo.nombre_cliente || supervisorNombre}
                </h2>
                <h3 className="text-md font-semibold text-green-600 mb-3">
                  {puestoInfo.descripcion || puestoInfo.nombre || 'Sin descripción'}
                </h3>
                <button
                  type="button"
                  onClick={handleCambiarPuesto}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  Seleccionar Nuevo Punto
                </button>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-medium text-green-800">{success}</p>
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <span className="ml-3 text-gray-600">Cargando puestos de servicio...</span>
              </div>
            ) : !selectedPuesto ? (
              <div>
                <label htmlFor="puesto" className="block text-sm font-semibold text-gray-700 mb-2">
                  Seleccionar Puesto de Servicio <span className="text-red-500">*</span>
                </label>
                <select
                  id="puesto"
                  value={selectedPuesto}
                  onChange={(e) => setSelectedPuesto(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Selecciona un puesto</option>
                  {puestos.map((puesto) => (
                    <option key={puesto.id} value={puesto.id}>
                      {puesto.nombre} {puesto.direccion ? `- ${puesto.direccion}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : checks.length === 0 ? (
              <div>
                <h2 className="text-xl font-bold text-red-600 mb-4">
                  No se ha encontrado una lista de supervisión, punto inhabilitado, por favor contactar administrador
                </h2>
                <button
                  type="button"
                  onClick={handleCambiarPuesto}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Seleccionar Nuevo Punto
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Vigilante de turno — siempre ingreso manual y verificación contra PHT */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Vigilante de turno <span className="text-red-500">*</span>
                  </label>
                  <p className="text-sm text-gray-600 mb-2">
                    Ingrese la cédula del vigilante y verifíquela contra el historial de trabajo del puesto.
                  </p>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={cedulaBusqueda}
                        onChange={(e) => {
                          setCedulaBusqueda(e.target.value);
                          if (verificacionVigilante) {
                            setVerificacionVigilante(null);
                            setPersonalEncontrado(null);
                            setDiscrepanciaInfo(null);
                            setAuditoriaDiscrepanciaId(null);
                          }
                        }}
                        placeholder="Ingrese la cédula"
                        disabled={vigilanteVerificado || buscandoPersonal || submitting || reportandoDiscrepancia}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && cedulaBusqueda.trim() && !vigilanteVerificado) {
                            e.preventDefault();
                            handleVerificarCedula();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleVerificarCedula}
                        disabled={!cedulaBusqueda.trim() || !selectedPuesto || buscandoPersonal || submitting || vigilanteVerificado || reportandoDiscrepancia}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                      >
                        {buscandoPersonal ? 'Verificando…' : 'Verificar'}
                      </button>
                    </div>

                    {personalEncontrado && verificacionVigilante === 'ok' && (
                      <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2">
                        <p className="text-sm font-medium text-green-800">Cédula verificada</p>
                        <p className="text-sm text-green-900 mt-1">{personalEncontrado.nombres}</p>
                      </div>
                    )}

                    {verificacionVigilante === 'discrepancia' && discrepanciaInfo && (
                      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 space-y-2">
                        <p className="text-sm font-semibold text-amber-900">Discrepancia detectada</p>
                        <p className="text-sm text-amber-900">
                          La cédula ingresada <strong>{discrepanciaInfo.ingresado.nombre}</strong> no corresponde
                          al vigilante en historial de trabajo{' '}
                          <strong>{discrepanciaInfo.pht.nombre}</strong>.
                        </p>
                        <button
                          type="button"
                          onClick={handleReportarDiscrepancia}
                          disabled={reportandoDiscrepancia}
                          className="w-full inline-flex justify-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                        >
                          {reportandoDiscrepancia ? 'Reportando…' : 'Reportar discrepancia y continuar'}
                        </button>
                      </div>
                    )}

                    {verificacionVigilante === 'reportada' && personalEncontrado && (
                      <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2">
                        <p className="text-sm font-medium text-green-800">
                          Discrepancia reportada{auditoriaDiscrepanciaId ? ` (#${auditoriaDiscrepanciaId})` : ''}. Puede continuar con la visita.
                        </p>
                        <p className="text-sm text-green-900 mt-1">{personalEncontrado.nombres}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Resto del formulario solo tras verificación OK o discrepancia reportada */}
                {vigilanteVerificado && personalEncontrado && (
                  <>
                    {/* Foto */}
                <div>
                      <label htmlFor="asistencia-foto" className="block text-sm font-medium text-gray-700 mb-1">
                        Foto <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <button
                            id={`${capturaInputId}-button`}
                            type="button"
                            onClick={() => document.getElementById(capturaInputId)?.click()}
                            className="inline-flex justify-center items-center w-full sm:w-[140px] px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60"
                        disabled={submitting}
                          >
                            Capturar foto
                          </button>
                        </div>

                      <input
                          id={capturaInputId}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={handleFileChange}
                        disabled={submitting}
                        />
                      </div>
                      {fotoPreview && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 mb-1">Previsualización:</p>
                          <img
                            src={fotoPreview}
                            alt="Foto de asistencia"
                            className="w-full max-w-xs rounded-lg border border-gray-200"
                          />
                  </div>
                      )}
                </div>

                {/* Lista de items */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Lista de items</h2>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Item
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Check
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {checks.map((check, index) => (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm text-gray-900">{check.descripcion}</td>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedChecks.some(c => c.id === check.id)}
                                onChange={() => handleCheckChange(check)}
                                disabled={submitting}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan="2" className="px-4 py-3">
                            <div>
                              <label htmlFor="comentarios" className="block text-sm font-medium text-gray-700 mb-2">
                                Comentarios <span className="text-red-500">*</span>
                              </label>
                              <textarea
                                id="comentarios"
                                value={comentarios}
                                onChange={(e) => setComentarios(e.target.value)}
                                rows={2}
                                required
                                disabled={submitting}
                                placeholder="Ingresa tus comentarios..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              />
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="2" className="px-4 py-3">
                            <div className="space-y-3 mb-4">
                              {gpsMessage && (
                                <div>
                                  <h4 className={`text-sm font-semibold mb-2 ${
                                    gpsMessage.includes('Error') || gpsMessage.includes('inválidas') 
                                      ? 'text-red-600' 
                                      : gpsMessage.includes('válidas') || gpsMessage.includes('excelente')
                                      ? 'text-green-600'
                                      : gpsMessage.includes('buena')
                                      ? 'text-blue-600'
                                      : gpsMessage.includes('aceptable')
                                      ? 'text-yellow-600'
                                      : gpsMessage.includes('baja') || gpsMessage.includes('muy baja')
                                      ? 'text-orange-600'
                                      : 'text-gray-600'
                                  }`}>
                                    {gpsMessage}
                                  </h4>
                                  {gpsAccuracy !== null && (
                                    <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-medium text-gray-700">Precisión GPS:</span>
                                          {gpsActive && watchingGps ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                              ● Activo
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                              ● Inactivo
                                            </span>
                                          )}
                                        </div>
                                        <span className={`text-sm font-bold ${
                                          gpsAccuracy < 10 ? 'text-green-600' :
                                          gpsAccuracy < 20 ? 'text-blue-600' :
                                          gpsAccuracy < maxAccuracyMeters ? 'text-yellow-600' :
                                          'text-orange-600'
                                        }`}>
                                          {gpsAccuracy.toFixed(1)} metros
                                        </span>
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                                        <div 
                                          className={`h-2.5 rounded-full transition-all duration-500 ${
                                            gpsAccuracy < 10 ? 'bg-green-500' :
                                            gpsAccuracy < 20 ? 'bg-blue-500' :
                                            gpsAccuracy < 50 ? 'bg-yellow-500' :
                                            'bg-orange-500'
                                          }`}
                                          style={{ 
                                            width: `${Math.min(100, Math.max(5, (100 - Math.min(gpsAccuracy, 100)) / 100 * 100))}%` 
                                          }}
                                        ></div>
                                      </div>
                                      <p className={`text-xs font-medium ${
                                        gpsAccuracy < 10 ? 'text-green-700' :
                                        gpsAccuracy < 20 ? 'text-blue-700' :
                                        gpsAccuracy < maxAccuracyMeters ? 'text-yellow-700' :
                                        'text-orange-700'
                                      }`}>
                                        {gpsAccuracy < 10 ? '✓ Precisión excelente - Ideal para registrar' :
                                         gpsAccuracy < 20 ? '✓ Precisión buena - Recomendado para registrar' :
                                         gpsAccuracy < maxAccuracyMeters ? '⚠ Precisión aceptable - Puedes registrar' :
                                         `⚠ Precisión insuficiente - Se requiere máximo ${maxAccuracyMeters} m (actual: ${gpsAccuracy.toFixed(1)} m)`}
                                      </p>
                                      {!gpsActive && (
                                        <p className="text-xs font-medium text-red-700 mt-2">
                                          ⚠ El GPS no está activo. Debes mantener el GPS activo para poder enviar el reporte.
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  {watchingGps && !gpsCoords && (
                                    <p className="text-xs text-gray-500 italic">
                                      Esperando señal GPS...
                                    </p>
                                  )}
                                </div>
                              )}
                              {(!watchingGps || !gpsCoords) && (
                                <div className="mb-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setError('');
                                      iniciarMonitoreoGPS();
                                    }}
                                    disabled={submitting}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    📍 Activar GPS
                                  </button>
                                  <p className="text-xs text-gray-500 mt-2">
                                    {simulatedGPS && simulatedGPS.enabled 
                                      ? 'GPS simulado activo. Haz clic para usar GPS real o simulado.'
                                      : 'Haz clic para activar el GPS y obtener tu ubicación. El navegador te pedirá permiso.'}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="flex justify-end">
                                <button
                                  type="submit"
                                  disabled={
                                    submitting || validatingGps ||
                                    !gpsCoords || !gpsActive || !watchingGps ||
                                    (gpsAccuracy !== null && gpsAccuracy > maxAccuracyMeters)
                                  }
                                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={
                                    !gpsCoords ? 'Esperando coordenadas GPS...' :
                                    !gpsActive || !watchingGps ? 'El GPS debe estar activo para enviar' :
                                    (gpsAccuracy !== null && gpsAccuracy > maxAccuracyMeters) ? `Precisión insuficiente (${gpsAccuracy.toFixed(1)} m). Se requiere máximo ${maxAccuracyMeters} m.` :
                                    'Enviar reporte'
                                  }
                              >
                                {submitting ? 'Enviando...' : 'Enviar'}
                              </button>
                            </div>
                            {(!gpsActive || !watchingGps) && gpsCoords && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-xs text-yellow-800">
                                  ⚠ El GPS se ha desactivado. Debes reactivar el GPS para poder enviar el reporte.
                                </p>
                              </div>
                            )}
                            {(gpsAccuracy !== null && gpsAccuracy > maxAccuracyMeters) && (
                              <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                                <p className="text-xs text-orange-800">
                                  ⚠ La precisión del GPS ({gpsAccuracy.toFixed(1)} m) es insuficiente. Se requiere máximo {maxAccuracyMeters} metros para enviar el reporte.
                                </p>
                              </div>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                  </>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Modal de éxito */}
        {showSuccessModal && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
            style={{ zIndex: 9999 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseSuccessModal();
              }
            }}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header con icono de éxito */}
              <div className="px-6 py-5 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">Registro exitoso</h3>
                    <p className="text-sm text-gray-600 mt-0.5">Asistencia registrada correctamente</p>
                  </div>
                </div>
              </div>

              {/* Contenido del modal */}
              <div className="px-6 py-5 space-y-4">
                {/* Información de la asistencia */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="space-y-3">
                    {asistenciaInfoModal.supervisor && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <div className="flex-1">
                          <span className="text-xs text-gray-500">Supervisor:</span>
                          <span className="text-sm font-semibold text-gray-900 ml-2">{asistenciaInfoModal.supervisor}</span>
                        </div>
                      </div>
                    )}
                    {asistenciaInfoModal.puesto && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div className="flex-1">
                          <span className="text-xs text-gray-500">Puesto:</span>
                          <span className="text-sm font-semibold text-gray-900 ml-2">{asistenciaInfoModal.puesto}</span>
                        </div>
                      </div>
                    )}
                    {asistenciaInfoModal.vigilante && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <div className="flex-1">
                          <span className="text-xs text-gray-500">Vigilante:</span>
                          <span className="text-sm font-semibold text-gray-900 ml-2">{asistenciaInfoModal.vigilante}</span>
                        </div>
                      </div>
                    )}
                    {(asistenciaInfoModal.fecha || asistenciaInfoModal.hora) && (
                      <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <div className="flex-1">
                          <span className="text-xs text-gray-500">
                            {asistenciaInfoModal.fecha && `Fecha: ${asistenciaInfoModal.fecha}`}
                            {asistenciaInfoModal.fecha && asistenciaInfoModal.hora && ' | '}
                            {asistenciaInfoModal.hora && `Hora: ${asistenciaInfoModal.hora}`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mensaje de confirmación */}
                <div className="text-center">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {success || 'La asistencia ha sido registrada correctamente en el sistema.'}
                  </p>
                </div>

                {/* Indicador visual */}
                <div className="flex items-center justify-center gap-2 pt-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-500">Registro confirmado</span>
                </div>
              </div>

              {/* Footer con botón */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseSuccessModal}
                  className="w-full inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Registrar Nuevo Puesto
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de error de validación */}
        {showErrorModal && error && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
            style={{ zIndex: 9999 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowErrorModal(false);
              }
            }}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header con icono de error */}
              <div className="px-6 py-5 bg-gradient-to-r from-red-50 to-rose-50 border-b border-red-100">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">Error de Validación</h3>
                    <p className="text-sm text-gray-600 mt-1">Por favor corrige los siguientes errores</p>
                  </div>
                </div>
              </div>

              {/* Contenido del modal */}
              <div className="px-6 py-5">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>

              {/* Footer del modal */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="w-full inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de error por distancia */}
        {showDistanceErrorModal && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
            style={{ zIndex: 9999 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDistanceErrorModal(false);
              }
            }}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header con icono de error */}
              <div className="px-6 py-5 bg-gradient-to-r from-red-50 to-rose-50 border-b border-red-100">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">Ubicación fuera de rango</h3>
                    <p className="text-sm text-gray-600 mt-0.5">Te encuentras muy alejado del puesto</p>
                  </div>
                </div>
              </div>

              {/* Contenido del modal */}
              <div className="px-6 py-5 space-y-4">
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-red-900">
                          Distancia actual: {distanceError.distance.toFixed(1)} metros
                        </p>
                      </div>
                    </div>
                    {distanceError.lat !== null && distanceError.lng !== null && (
                      <div className="mt-3 pt-3 border-t border-red-200">
                        <p className="text-xs text-red-800 font-medium mb-1">Coordenadas GPS reportadas:</p>
                        <div className="flex flex-col gap-1">
                          <p className="text-xs text-red-700">
                            <span className="font-semibold">Latitud:</span> {distanceError.lat.toFixed(6)}
                          </p>
                          <p className="text-xs text-red-700">
                            <span className="font-semibold">Longitud:</span> {distanceError.lng.toFixed(6)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mensaje de error */}
                <div className="text-center">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Te encuentras a <span className="font-bold text-red-600">{distanceError.distance.toFixed(1)} metros</span> del sitio. 
                    Por favor, acércate más al puesto de servicio para poder registrar asistencia.
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Vuelve a intentar cuando estés más cerca del puesto.
                  </p>
                </div>

                {/* Indicador visual */}
                <div className="flex items-center justify-center gap-2 pt-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-500">Ubicación no válida</span>
                </div>
              </div>

              {/* Footer con botón */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowDistanceErrorModal(false)}
                  className="w-full inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RegistrarAsistencia;
